import React, { useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { AvatarWithoutDot } from '@components/Avatar/Avatar'
import { ReviewStars } from '@components/ReviewStars/ReviewStars'
import { userIdFromAddress } from '@towns-protocol/sdk'
import { bin_fromHexString } from '@towns-protocol/utils'
import { Box, Paragraph, Stack, Text } from '@ui'
import { useBotReviews } from 'hooks/useBotReviews'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { Address, useGetRootKeyFromLinkedWallet, useUserLookup } from 'use-towns-client'
import { formatRelativeTime } from 'utils/formatDates'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'
import { shortAddress } from 'workers/utils'

interface BotReviewsSectionProps {
    appAddress: Address | undefined
    showSummary?: boolean
}

const REVIEWS_PER_PAGE = 20

interface ReviewSummaryProps {
    reviewCount: number
    overallRating: number
    ratingDistribution: Array<{ stars: number; percentage: number }>
}

const ReviewSummary = ({ reviewCount, overallRating, ratingDistribution }: ReviewSummaryProps) => {
    const formattedRating = overallRating.toFixed(1)

    return (
        <Stack gap="md">
            <Stack gap>
                <Stack horizontal gap="x4">
                    <Stack grow gap minWidth="200" maxWidth="400">
                        <Box horizontal gap="sm" alignItems="center">
                            <ReviewStars rating={overallRating} size={16} />
                            <Text strong>{formattedRating}</Text>
                            <Paragraph strong>Overall</Paragraph>
                        </Box>
                        <Stack gap="sm">
                            {ratingDistribution.map(({ stars, percentage }) => (
                                <Stack horizontal key={stars} gap="xs" alignItems="center">
                                    <Box justifyContent="center" minWidth="x6">
                                        <Paragraph size="sm" whiteSpace="nowrap">
                                            {stars} Star{stars === 1 ? '' : 's'}
                                        </Paragraph>
                                    </Box>
                                    <Box grow>
                                        <Box
                                            grow
                                            height="x1"
                                            background="level3"
                                            borderRadius="sm"
                                            position="relative"
                                            overflow="hidden"
                                            rounded="md"
                                        >
                                            <Box
                                                rounded="md"
                                                height="x1"
                                                background="botGradient"
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </Box>
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )
}

const ReviewAuthorDisplay = ({
    address,
    clickableUser,
    handleOpenProfile,
}: {
    address: string
    clickableUser?: boolean
    handleOpenProfile?: () => void
}) => {
    const user = useUserLookup(address)
    const name = !user ? shortAddress(address) : getPrettyDisplayName(user)

    return (
        <Box
            tooltip={address}
            cursor={clickableUser ? 'pointer' : 'default'}
            onClick={handleOpenProfile}
        >
            <Text strong>{name}</Text>
        </Box>
    )
}

interface BotReviewItemProps {
    review: {
        text: string
        rating: number
        reviewerAddress: string
        timestamp?: number
    }
    clickableUser?: boolean
}

const BotReviewItem = ({ review, clickableUser = false }: BotReviewItemProps) => {
    const { openPanel } = usePanelActions()
    const { data: rootKeyAddress } = useGetRootKeyFromLinkedWallet({
        walletAddress: review.reviewerAddress as Address,
    })
    const userId = rootKeyAddress ? userIdFromAddress(bin_fromHexString(rootKeyAddress)) : undefined

    const handleOpenProfile = () => {
        if (clickableUser && userId) {
            openPanel('profile', { profileId: userId })
        }
    }

    return (
        <Stack position="relative">
            <Stack horizontal gap="sm" alignItems="start">
                <Box cursor={clickableUser ? 'pointer' : 'default'} onClick={handleOpenProfile}>
                    <AvatarWithoutDot userId={review.reviewerAddress} size="avatar_md" />
                </Box>
                <Stack grow gap="sm">
                    <ReviewStars rating={review.rating} size={16} />
                    <Text>{review.text}</Text>
                    <Stack horizontal gap="sm" alignItems="center">
                        <ReviewAuthorDisplay
                            address={review.reviewerAddress}
                            clickableUser={clickableUser}
                            handleOpenProfile={handleOpenProfile}
                        />
                        {review.timestamp && (
                            <Text color="gray2">{formatRelativeTime(review.timestamp)}</Text>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )
}

export const BotReviewsSection = ({ appAddress, showSummary }: BotReviewsSectionProps) => {
    const { data: reviewData, isLoading } = useBotReviews(appAddress)
    const [visibleReviews, setVisibleReviews] = useState(REVIEWS_PER_PAGE)

    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0.5,
        triggerOnce: false,
    })

    const reviews = useMemo(() => reviewData?.reviews ?? [], [reviewData?.reviews])
    const totalCount = reviewData?.totalCount ?? 0
    const averageRating = reviewData?.summary?.averageRating ?? 0

    useEffect(() => {
        if (inView && visibleReviews < reviews.length) {
            setVisibleReviews((prev) => Math.min(prev + REVIEWS_PER_PAGE, reviews.length))
        }
    }, [inView, visibleReviews, reviews.length])

    // Calculate rating distribution from actual reviews
    const ratingDistribution = useMemo(() => {
        const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
        reviews.forEach((review) => {
            const rating = Math.round(review.rating) as 1 | 2 | 3 | 4 | 5
            if (rating >= 1 && rating <= 5) {
                counts[rating]++
            }
        })

        return [5, 4, 3, 2, 1].map((stars) => ({
            stars,
            percentage:
                reviews.length > 0
                    ? Math.round((counts[stars as keyof typeof counts] * 100) / reviews.length)
                    : 0,
        }))
    }, [reviews])

    if (isLoading) {
        return null
    }

    if (totalCount === 0) {
        return null
    }

    return (
        <Stack gap="md">
            <Text strong size="lg">
                {totalCount} {totalCount === 1 ? 'Review' : 'Reviews'}
            </Text>

            {showSummary && (
                <ReviewSummary
                    reviewCount={totalCount}
                    overallRating={averageRating}
                    ratingDistribution={ratingDistribution}
                />
            )}

            <Stack gap="sm">
                {reviews.slice(0, visibleReviews).map((review) => (
                    <Box key={review.reviewerAddress} paddingY="sm">
                        <BotReviewItem review={review} />
                    </Box>
                ))}
                {visibleReviews < reviews.length && <Box ref={loadMoreRef} height="x4" />}
            </Stack>
        </Stack>
    )
}
